import { inject } from '@angular/core';
import { Router, type CanActivateFn } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { LoggerService } from '../services/logger.service';

/**
 * Auth guard to protect routes that require authentication
 *
 * CRITICAL: This guard should NEVER clear auth tokens on database errors.
 * Only clear tokens if they are malformed or expired.
 * Database errors are temporary and should not invalidate valid JWTs.
 */
export const authGuard: CanActivateFn = async (route, state) => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const logger = inject(LoggerService).getLogger('auth-guard');

  logger.debug('Checking auth for route', state.url);

  const isAuth = authService.isAuthenticated();
  logger.debug('Is authenticated?', isAuth);

  if (isAuth) {
    logger.debug('Allowing access');
    return true;
  }

  // Check if there's an auth token
  const token = authService.getToken();
  logger.debug('Has auth token?', !!token);

  if (!token) {
    // No auth token - redirect to landing page
    logger.info('No auth token - redirecting to /landing');
    router.navigate(['/landing'], {
      queryParams: { returnUrl: state.url }
    });
    return false;
  }

  // Validate token is not expired
  if (!authService.isTokenValid(token)) {
    logger.warn('Token is expired or malformed - clearing and redirecting to /landing');
    logger.warn('Token clear reason: expiration/malformed in auth guard');
    authService.clearToken();
    router.navigate(['/landing'], {
      queryParams: { returnUrl: state.url }
    });
    return false;
  }

  // Token is valid - try to restore auth state from it
  try {
    const payload = authService.decodeJwt(token);

    // Set minimal user state to unblock UI
    authService.currentUser.set({
      id: payload.sub,
      email: payload.email,
      full_name: payload.full_name,
    });
    authService.isAuthenticated.set(true);
    logger.debug('Auth state restored from valid token');

    // Optionally check database membership (non-blocking)
    // If this fails, it's a database issue, not an auth issue
    try {
      const isActiveMember = await authService.isActiveMemberOfOrg();
      if (!isActiveMember) {
        logger.warn('User has valid token but no active org membership');
        // Don't clear token - user might need to create/join an org
        router.navigate(['/organizations'], {
          queryParams: { returnUrl: state.url }
        });
        return false;
      }
    } catch (dbError) {
      // Database error - log but don't block access
      // User has valid token, so let them through
      logger.warn('Database check failed but token is valid - allowing access:', dbError);
      // Full user data will be loaded by UserResolver when database is ready
    }

    return true;
  } catch (error) {
    // Token decoding failed - token is malformed
    logger.error('Failed to decode token:', error);
    logger.warn('Token clear reason: decode failure in auth guard');
    authService.clearToken();
    router.navigate(['/landing'], {
      queryParams: { returnUrl: state.url }
    });
    return false;
  }
};
